/**
 * useSizeIndicator Hook
 *
 * Simplified hook that subscribes to the sizeInfo from the designStore.
 * The actual calculation is done in the store via updateSizeInfo().
 *
 * This hook is now just a convenient selector for components that need
 * the size information of the currently selected object.
 *
 * @see stores/designStore.ts - updateSizeInfo() action
 * @see utils/sizeCalculation.ts - Pure calculation functions
 */

import { useDesignStore } from '@/stores';

// Re-export SizeInfo type for convenience
export type { SizeInfo } from '@/utils/sizeCalculation';

/**
 * Hook to get size information for the currently selected canvas object
 *
 * Usage:
 * ```tsx
 * const { sizeInfo, hasSelection } = useSizeIndicator();
 * if (sizeInfo) {
 *   console.log(`${sizeInfo.width} x ${sizeInfo.height} ${sizeInfo.unit}`);
 * }
 * ```
 *
 * Note: The size info is automatically updated when the store's updateSizeInfo()
 * action is called. Components that modify objects should call updateSizeInfo()
 * after making changes.
 */
export function useSizeIndicator() {
  const sizeInfo = useDesignStore((state) => state.sizeInfo);

  return {
    sizeInfo,
    hasSelection: sizeInfo !== null,
  };
}
